/**
 * DDI — Índice divergência direcional 2.
 *
 * Migrado do Silverlight (GraficoDePrata/Formulas/FMLTrend.cs, classe FML.DDI —
 * FALSO-AMIGO do `didi`/DIDI Linhas do odin, sem relação de fórmula). Painel
 * próprio (PlotInNewAxis=true), histograma + 2 linhas (estilo MACD):
 *
 *   TR  = MAX(|H−REF(H,1)|, |L−REF(L,1)|)
 *   DMZ = IF((H+L) <= (REF(H,1)+REF(L,1)), 0, TR)
 *   DMF = IF((H+L) >= (REF(H,1)+REF(L,1)), 0, TR)
 *   DIZ = SUM(DMZ,N) / (SUM(DMZ,N)+SUM(DMF,N))
 *   DIF = SUM(DMF,N) / (SUM(DMF,N)+SUM(DMZ,N))
 *   DDI  = DIZ − DIF            (histograma COLORSTICK — colore por sinal)
 *   ADDI = SMA(DDI, N1, M)      (SMA-chinesa = DMA(DDI, M/N1, N1))
 *   AD   = MA(ADDI, M1)         (média aritmética)
 *
 * Pacote do engine = [DDI, ADDI, AD]. Todos os primitivos são ports auditados
 * do engine (silverlightDsl). Paridade provada em __tests__/ddi.silverlight.test.js.
 */
import {
  ref,
  max2,
  abs,
  sub,
  add,
  div,
  iff,
  le,
  ge,
  sum,
  sma,
  ma,
} from "./silverlightDsl";
import { DDI as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { n, n1, m, m1 } = options;
    const toNum = (v) => (Number.isFinite(v) ? v : NaN);
    const H = data.map((d) => toNum(d.high));
    const L = data.map((d) => toNum(d.low));

    const refH = ref(H, 1);
    const refL = ref(L, 1);
    const tr = max2(abs(sub(H, refH)), abs(sub(L, refL)));
    const hl = add(H, L);
    const refhl = add(refH, refL);
    const dmz = iff(le(hl, refhl), 0, tr);
    const dmf = iff(ge(hl, refhl), 0, tr);
    const sumZ = sum(dmz, n);
    const sumF = sum(dmf, n);
    const diz = div(sumZ, add(sumZ, sumF));
    const dif = div(sumF, add(sumF, sumZ));
    const ddi = sub(diz, dif);
    const addi = sma(ddi, n1, m);
    const ad = ma(addi, m1);

    return ddi.map((v, i) => {
      const value = Number.isFinite(v) ? v : undefined;
      const addiV = Number.isFinite(addi[i]) ? addi[i] : undefined;
      const adV = Number.isFinite(ad[i]) ? ad[i] : undefined;
      if (value === undefined && addiV === undefined && adV === undefined)
        return undefined;
      // { value, direction } p/ o histograma (COLORSTICK por sinal) + 2 linhas.
      return { value, direction: v > 0 ? "up" : "down", addi: addiV, ad: adV };
    });
  }

  calculator.undefinedLength = function () {
    const { n, n1, m1 } = options;
    // DDI ~ n+2 (SUM emite a partir de i>N); ADDI acrescenta n1; AD acrescenta m1.
    return Math.max(0, n + 2 + n1 + m1);
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
